// Frame scheduling. Every frame renders in a requestAnimationFrame callback, so the browser's
// compositor paces presentation and input events always get their turn between frames. A browser
// runs rAF at the display's refresh rate; the desktop app (desktop/) turns vsync and the frame-rate
// limit off, so there rAF runs as fast as the GPU allows. A cap renders only the animation frames
// that keep the rate at or under it.
//
// Nothing renders outside animation frames: a vsynced compositor never shows those frames, and their
// GPU work queues ahead of the frame it does show, so input lags further behind the longer it runs.
//
// The next animation frame is requested from an ordinary task posted when a frame starts, not from
// the frame itself. While the mouse moves, Chromium's main-thread scheduler runs rendering ahead of
// every ordinary task; with a frame always pending (uncapped rAF, or frames that take longer than the
// refresh interval while shooting) the network tasks then never ran: no snapshot was handled and no
// WebTransport write completed for seconds (docs/NETCODE.md "Main-thread scheduling"). The posted
// task runs after the network tasks that were queued before it, so each frame lets them through.

/** rAF gaps longer than this (a hidden tab, a debugger pause) do not count towards the rAF interval. */
const MAX_RAF_GAP_MS = 100;

export class FrameLoop {
  private running = false;
  private rafId = 0;
  /** Minimum ms between rendered frames; 0 renders every animation frame. */
  private interval = 0;
  /** rAF time at or after which the next capped frame renders. */
  private next = 0;
  private lastRaf = 0;
  /** Smoothed ms between animation frames. */
  private rafInterval = 0;
  /** Carries the task that requests the next animation frame. */
  private readonly hop = new MessageChannel();
  private hopPending = false;

  /** `nudge` runs on every animation frame, rendered or skipped (see `layerNudge`). */
  constructor(
    private readonly callback: (nowMs: number) => void,
    private readonly nudge: () => void = () => undefined,
  ) {
    this.hop.port1.onmessage = this.onHop;
  }

  get active(): boolean {
    return this.running;
  }

  /** `cap`: see `setCap`. */
  start(cap: number | null): void {
    this.setCap(cap);
    if (this.running) return;
    this.running = true;
    this.lastRaf = 0;
    this.requestFrame();
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;
    cancelAnimationFrame(this.rafId);
    this.rafId = 0;
  }

  /** Most frames per second to render, or null for every animation frame. Applies from the next frame. */
  setCap(cap: number | null): void {
    const interval = cap && cap > 0 ? 1000 / cap : 0;
    if (interval === this.interval) return;
    this.interval = interval;
    this.next = 0;
  }

  /** Requests the next animation frame from a task queued behind the ones already waiting. */
  private requestFrame(): void {
    if (this.hopPending) return;
    this.hopPending = true;
    this.hop.port2.postMessage(null);
  }

  private readonly onHop = (): void => {
    this.hopPending = false;
    if (this.running && this.rafId === 0) this.rafId = requestAnimationFrame(this.onFrame);
  };

  private readonly onFrame = (time: number): void => {
    this.rafId = 0;
    this.requestFrame();
    this.nudge();
    const gap = time - this.lastRaf;
    if (this.lastRaf > 0 && gap < MAX_RAF_GAP_MS) {
      this.rafInterval = this.rafInterval > 0 ? this.rafInterval + (gap - this.rafInterval) * 0.1 : gap;
    }
    this.lastRaf = time;
    if (this.interval > 0) {
      // Render the animation frame nearest the deadline: up to half an rAF interval early, so a cap
      // at the display's rate keeps every frame despite rAF jitter.
      const early = Math.min(this.rafInterval, this.interval) / 2;
      if (time + early < this.next) return;
      // Keep the cadence; after a stall, restart it instead of rendering a burst to catch up.
      this.next = this.next + this.interval > time ? this.next + this.interval : time + this.interval;
    }
    this.callback(performance.now());
  };
}

/**
 * Keeps uncapped animation frames back to back. With vsync off (the desktop app), Chromium follows
 * an animation frame that changes nothing on screen with a ~60 Hz idle tick, and the game canvas is
 * desynchronized, so its own frames do not count as a change: frames stalled for ~17 ms, and a cap
 * that skips animation frames stalled on every skip. Moving a 1 px layer on every animation frame is
 * a change that costs no paint. Desktop app only: in a vsynced browser the extra commit per frame
 * showed DOM updates a frame later. The layer is created on the first call.
 */
export function layerNudge(): () => void {
  let el: HTMLDivElement | null = null;
  let shifted = false;
  return () => {
    if (el === null) {
      el = document.createElement("div");
      el.style.cssText = "position:fixed;left:0;top:0;width:1px;height:1px;opacity:0.01;pointer-events:none;will-change:transform";
      document.body.appendChild(el);
    }
    shifted = !shifted;
    el.style.transform = shifted ? "translateX(0.5px)" : "";
  };
}
